/**
 * TASK-301: Factures Récurrentes - Liste des templates
 * Architecture 3 couches: Component (UI) → Hook (Logic) → API Layer (HTTP)
 * V2 DESIGN SYSTEM - Sprint 3
 *
 * Refactored: TanStack Query (useQuery + useMutation), shared formatters, server pagination
 */

import {
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  Eye,
  MoreVertical,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Repeat,
  Trash2,
  XCircle,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { confirm } from "../../../stores/confirmStore";
import { formatCurrency, formatDate } from '@/utils/vatCalculations';
import type {
  RecurringInvoiceStatus,
} from "../types/billing.types";

// Hooks TanStack Query
import {
  useRecurringInvoices,
  useRecurringInvoiceStats,
  usePauseRecurringInvoice,
  useResumeRecurringInvoice,
  useCancelRecurringInvoice,
  useGenerateInvoice,
  useDeleteRecurringInvoice,
} from '../hooks/useRecurringInvoices';

// V2 DESIGN SYSTEM IMPORTS
import {
  Button,
  Badge,
  DropdownMenu,
  type DropdownMenuItem,
} from '@/components/ui-v2';

import {
  FilterBar,
  FilterOption,
} from '@/components/ui';



export const RecurringInvoicesList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Status configurations (inside component to access t())
  const getStatusConfig = (status: RecurringInvoiceStatus) => {
    const configs: Record<RecurringInvoiceStatus, { label: string; color: string; icon: React.ReactNode }> = {
      ACTIVE: {
        label: t('billing.recurringStatus.ACTIVE'),
        color: 'bg-teal-50 text-teal-700',
        icon: <CheckCircle className="w-4 h-4" />,
      },
      PAUSED: {
        label: t('billing.recurringStatus.PAUSED'),
        color: 'bg-amber-50 text-amber-700',
        icon: <Pause className="w-4 h-4" />,
      },
      COMPLETED: {
        label: t('billing.recurringStatus.COMPLETED'),
        color: 'bg-gray-100 text-gray-600',
        icon: <CheckCircle className="w-4 h-4" />,
      },
      CANCELLED: {
        label: t('billing.recurringStatus.CANCELLED'),
        color: 'bg-red-50 text-red-600',
        icon: <XCircle className="w-4 h-4" />,
      },
    };
    return configs[status] || configs.ACTIVE;
  };

  // Server state via TanStack Query (F1 fix)
  const { data: templatesResponse, isLoading: loading, error: queryError } = useRecurringInvoices({
    page: 1,
    limit: 50,
  });
  const { data: stats } = useRecurringInvoiceStats();

  // Mutations via useMutation (F7 fix)
  const pauseMutation = usePauseRecurringInvoice();
  const resumeMutation = useResumeRecurringInvoice();
  const cancelMutation = useCancelRecurringInvoice();
  const generateMutation = useGenerateInvoice();
  const deleteMutation = useDeleteRecurringInvoice();

  // Local UI state
  const [activeStatuses, setActiveStatuses] = useState<RecurringInvoiceStatus[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const templates = templatesResponse?.data || [];

  // Client-side search filter (status filtering done server-side via statusFilter)
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      // Additional client-side status filter for multi-status
      if (activeStatuses.length > 0 && !activeStatuses.includes(template.status)) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchName = template.name?.toLowerCase().includes(query);
        const matchClient = template.Client?.companyName?.toLowerCase().includes(query) ||
                          template.Client?.fullName?.toLowerCase().includes(query);
        if (!matchName && !matchClient) {
          return false;
        }
      }
      return true;
    });
  }, [templates, activeStatuses, searchQuery]);

  // Status counts for filters
  const statusCounts = useMemo(() => {
    return templates.reduce((acc, template) => {
      const status = template.status || 'ACTIVE';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [templates]);

  // Actions via useMutation
  const handlePause = useCallback(async (template: { id: string; name: string }) => {
    const confirmed = await confirm({
      title: t('billing.recurring.pauseTitle'),
      message: t('billing.recurring.pauseMessage', { name: template.name }),
      confirmText: t('billing.recurring.pauseTitle'),
      cancelText: t('common.cancel'),
      variant: "warning",
    });
    if (!confirmed) return;

    pauseMutation.mutate(template.id, {
      onError: (err) => setError(err instanceof Error ? err.message : t('billing.recurring.pauseError')),
    });
  }, [pauseMutation, t]);

  const handleResume = useCallback(async (template: { id: string; name: string }) => {
    const confirmed = await confirm({
      title: t('billing.recurring.resumeTitle'),
      message: t('billing.recurring.resumeMessage', { name: template.name }),
      confirmText: t('billing.recurring.resumeTitle'),
      cancelText: t('common.cancel'),
      variant: "info",
    });
    if (!confirmed) return;

    resumeMutation.mutate(template.id, {
      onError: (err) => setError(err instanceof Error ? err.message : t('billing.recurring.resumeError')),
    });
  }, [resumeMutation, t]);

  const handleCancel = useCallback(async (template: { id: string; name: string }) => {
    const confirmed = await confirm({
      title: t('common.cancel'),
      message: t('billing.recurring.cancelMessage', { name: template.name }),
      confirmText: t('billing.recurring.cancelConfirm'),
      cancelText: t('billing.recurring.cancelKeep'),
      variant: "danger",
    });
    if (!confirmed) return;

    cancelMutation.mutate(template.id, {
      onError: (err) => setError(err instanceof Error ? err.message : t('billing.recurring.cancelError')),
    });
  }, [cancelMutation, t]);

  const handleGenerateInvoice = useCallback(async (template: { id: string; name: string }) => {
    const confirmed = await confirm({
      title: t('billing.recurring.generateTitle'),
      message: t('billing.recurring.generateMessage', { name: template.name }),
      confirmText: t('common.generate'),
      cancelText: t('common.cancel'),
      variant: "info",
    });
    if (!confirmed) return;

    generateMutation.mutate(template.id, {
      onSuccess: (invoice) => {
        confirm({
          title: t('billing.recurring.generateSuccessTitle'),
          message: t('billing.recurring.generateSuccessMessage', { number: (invoice as any).number || '' }),
          confirmText: t('common.ok'),
          variant: "info",
        });
      },
      onError: (err) => setError(err instanceof Error ? err.message : t('billing.recurring.generateError')),
    });
  }, [generateMutation, t]);

  const handleDelete = useCallback(async (template: { id: string; name: string; status: RecurringInvoiceStatus }) => {
    if (template.status !== 'CANCELLED') {
      await confirm({
        title: t('billing.recurring.deleteImpossibleTitle'),
        message: t('billing.recurring.deleteImpossibleMessage'),
        confirmText: t('common.understood'),
        variant: "warning",
      });
      return;
    }

    const confirmed = await confirm({
      title: t('common.delete'),
      message: t('billing.recurring.deleteMessage', { name: template.name }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
      variant: "danger",
    });
    if (!confirmed) return;

    deleteMutation.mutate(template.id, {
      onError: (err) => setError(err instanceof Error ? err.message : t('billing.recurring.deleteError')),
    });
  }, [deleteMutation, t]);

  // Build dropdown menu items
  const getMenuItems = useCallback((template: { id: string; name: string; status: RecurringInvoiceStatus }): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [
      {
        label: t('common.view'),
        icon: <Eye className="w-4 h-4" />,
        onClick: () => navigate(`/recurring-invoices/${template.id}`),
      },
      {
        label: t('common.edit'),
        icon: <Edit className="w-4 h-4" />,
        onClick: () => navigate(`/recurring-invoices/${template.id}/edit`),
        disabled: template.status === 'CANCELLED',
      },
    ];

    if (template.status === 'ACTIVE') {
      items.push(
        {
          label: t('billing.recurring.generateTitle'),
          icon: <RefreshCw className="w-4 h-4" />,
          onClick: () => handleGenerateInvoice(template),
        },
        {
          label: t('billing.recurring.pauseTitle'),
          icon: <Pause className="w-4 h-4" />,
          onClick: () => handlePause(template),
        }
      );
    }

    if (template.status === 'PAUSED') {
      items.push({
        label: t('billing.recurring.resumeTitle'),
        icon: <Play className="w-4 h-4" />,
        onClick: () => handleResume(template),
      });
    }

    if (template.status !== 'CANCELLED' && template.status !== 'COMPLETED') {
      items.push({
        label: t('common.cancel'),
        icon: <XCircle className="w-4 h-4" />,
        onClick: () => handleCancel(template),
        variant: 'danger' as const,
      });
    }

    if (template.status === 'CANCELLED') {
      items.push({
        label: t('common.delete'),
        icon: <Trash2 className="w-4 h-4" />,
        onClick: () => handleDelete(template),
        variant: 'danger' as const,
      });
    }

    return items;
  }, [navigate, handlePause, handleResume, handleCancel, handleGenerateInvoice, handleDelete]);

  // Status filter options
  const statusFilterOptions: FilterOption[] = [
    { value: 'ACTIVE', label: t('billing.recurringStatus.ACTIVE'), count: statusCounts['ACTIVE'] || 0 },
    { value: 'PAUSED', label: t('billing.recurringStatus.PAUSED'), count: statusCounts['PAUSED'] || 0 },
    { value: 'COMPLETED', label: t('billing.recurringStatus.COMPLETED'), count: statusCounts['COMPLETED'] || 0 },
    { value: 'CANCELLED', label: t('billing.recurringStatus.CANCELLED'), count: statusCounts['CANCELLED'] || 0 },
  ];

  const displayError = error || (queryError instanceof Error ? queryError.message : queryError ? t('common.errorOccurred') : null);

  return (
    <div className="px-8 pt-8 pb-12 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900">
            {t('billing.recurring.title')}
          </h2>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            {filteredTemplates.length === templates.length
              ? t('billing.recurring.totalCount', { count: templates.length })
              : t('billing.recurring.filteredCount', { filtered: filteredTemplates.length, total: templates.length })}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate('/recurring-invoices/new')}
          icon={<Plus className="w-5 h-5" />}
        >
          {t('billing.recurring.newTemplate')}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-white/30 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-xl">
                <Repeat className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('billing.recurring.activeTemplates')}</p>
                <p className="text-xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-white/30 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-xl">
                <Pause className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('billing.recurringStatus.PAUSED')}</p>
                <p className="text-xl font-bold text-gray-900">{stats.paused}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-white/30 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-xl">
                <CheckCircle className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('common.total')}</p>
                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-white/30 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-xl">
                <Repeat className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">MRR</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(stats.monthlyRecurringRevenue)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {displayError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {displayError}
          <button
            onClick={() => setError(null)}
            className="ml-2 underline hover:no-underline"
          >
            {t('common.close')}
          </button>
        </div>
      )}

      {/* Filters */}
      {!loading && (
        <FilterBar
          searchPlaceholder={t('billing.recurring.searchPlaceholder')}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilters={statusFilterOptions}
          activeStatuses={activeStatuses}
          onStatusToggle={(status) => {
            setActiveStatuses(prev =>
              prev.includes(status as RecurringInvoiceStatus)
                ? prev.filter(s => s !== status)
                : [...prev, status as RecurringInvoiceStatus]
            );
          }}
          onClearFilters={() => {
            setSearchQuery('');
            setActiveStatuses([]);
          }}
          totalItems={templates.length}
          filteredItems={filteredTemplates.length}
        />
      )}

      {/* Main Content Container */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && templates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-8">
            <Repeat className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('billing.recurring.emptyTitle')}
            </h3>
            <p className="text-gray-600 mb-6 text-center">
              {t('billing.recurring.emptyDescription')}
            </p>
            <Button
              variant="secondary"
              onClick={() => navigate('/recurring-invoices/new')}
              icon={<Plus className="w-5 h-5" />}
            >
              {t('billing.recurring.createTemplate')}
            </Button>
          </div>
        )}

        {/* No results after filtering */}
        {!loading && templates.length > 0 && filteredTemplates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-8">
            <Repeat className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('common.noResults')}
            </h3>
            <p className="text-gray-600 mb-6 text-center">
              {t('billing.recurring.noResultsDescription')}
            </p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setActiveStatuses([]);
              }}
              variant="outline"
            >
              {t('common.clearFilters')}
            </Button>
          </div>
        )}

        {/* Templates Grid - Mobile Cards / Desktop Table */}
        {!loading && filteredTemplates.length > 0 && (
          <>
            {/* Mobile Card View */}
            <div className="grid grid-cols-1 gap-4 md:hidden p-6">
              {filteredTemplates.map((template) => {
                const statusConfig = getStatusConfig(template.status);
                return (
                  <div
                    key={template.id}
                    onClick={() => navigate(`/recurring-invoices/${template.id}`)}
                    className="bg-white/20 backdrop-blur-xl border border-white/30 rounded-2xl overflow-hidden hover:bg-white/35 hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  >
                    <div className="bg-white/10 backdrop-blur-sm p-4 border-b border-white/20">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">
                            {template.name}
                          </h3>
                          <p className="text-sm text-gray-600 truncate">
                            {template.Client?.companyName || template.Client?.fullName || '-'}
                          </p>
                        </div>
                        <div className="relative" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu
                            items={getMenuItems(template)}
                            trigger={
                              <button className="p-2 hover:bg-white/30 hover:backdrop-blur-sm rounded-lg transition-all duration-300">
                                <MoreVertical className="w-5 h-5 text-gray-600" />
                              </button>
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Repeat className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {t(`billing.frequency.${template.frequency}`)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('common.totalTTC')}</span>
                        <span className="text-lg font-bold text-gray-900">
                          {formatCurrency(template.totalTTC)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {t('billing.recurring.next')}: {formatDate(template.nextInvoiceDate)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <Badge className={statusConfig.color}>
                          {statusConfig.icon}
                          <span className="ml-1">{statusConfig.label}</span>
                        </Badge>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{t('billing.recurring.generatedCount', { count: template.generatedCount })}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/20">
                  <thead className="bg-white/10 backdrop-blur-sm">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.name')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.client')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.recurring.frequency')}</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.amountTTC')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.recurring.nextInvoice')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.labels.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {filteredTemplates.map((template) => {
                      const statusConfig = getStatusConfig(template.status);
                      return (
                        <tr key={template.id} className="hover:bg-white/10 transition-all duration-300">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{template.name}</div>
                            {template.description && (
                              <div className="text-sm text-gray-500 truncate max-w-[200px]">{template.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {template.Client?.companyName || template.Client?.fullName || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Repeat className="w-4 h-4 text-gray-400" />
                              <span>{t(`billing.frequency.${template.frequency}`)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                            {formatCurrency(template.totalTTC)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span>{formatDate(template.nextInvoiceDate)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={statusConfig.color}>
                              {statusConfig.icon}
                              <span className="ml-1">{statusConfig.label}</span>
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => navigate(`/recurring-invoices/${template.id}`)}
                                className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                                title={t('common.view')}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => navigate(`/recurring-invoices/${template.id}/edit`)}
                                className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                                title={t('common.edit')}
                                disabled={template.status === 'CANCELLED'}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {template.status === 'ACTIVE' && (
                                <>
                                  <button
                                    onClick={() => handleGenerateInvoice(template)}
                                    className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                                    title={t('billing.recurring.generateTitle')}
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handlePause(template)}
                                    className="p-2 text-amber-600 hover:text-amber-900 hover:bg-amber-50 rounded-lg transition-colors"
                                    title={t('billing.recurring.pauseTitle')}
                                  >
                                    <Pause className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              {template.status === 'PAUSED' && (
                                <button
                                  onClick={() => handleResume(template)}
                                  className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                                  title={t('billing.recurring.resumeTitle')}
                                >
                                  <Play className="w-4 h-4" />
                                </button>
                              )}
                              {template.status !== 'CANCELLED' && template.status !== 'COMPLETED' && (
                                <button
                                  onClick={() => handleCancel(template)}
                                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                                  title={t('common.cancel')}
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              )}
                              {template.status === 'CANCELLED' && (
                                <button
                                  onClick={() => handleDelete(template)}
                                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                                  title={t('common.delete')}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecurringInvoicesList;
